import React, { useState } from 'react';
import { Box, Card, Typography, Grid, Paper, CircularProgress, Button } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useQuery } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import { fetchWithToken } from "api/data";
import { DashboardStats } from "api/dashboard/dashboard.pb";
import { default as routes } from "api/routes";
import BusinessIcon from '@mui/icons-material/Business';
import DevicesIcon from '@mui/icons-material/Devices';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';

import publishArticleImage from './welcome_illustration.svg';
import DashboardCreationWizardMUI from "components/dashboard/create.wizard";
import DashboardsListNew from "components/dashboard/list";

const StatCard = ({ title, value, icon, color }: { 
    title: string; 
    value: number; 
    icon: React.ReactNode; 
    color: string;
}) => {
    return (
        <Paper
            elevation={3}
            sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                color: 'white',
                borderRadius: 2,
                minHeight: '120px',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '50px',
                    height: '50px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    transform: 'translate(25px, -25px)',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '30px',
                    height: '30px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '50%',
                    transform: 'translate(-15px, 15px)',
                }
            }}
        >
            <Box>
                <Typography variant="h3" component="div" fontWeight="bold">
                    {value.toLocaleString()}
                </Typography>
                <Typography variant="h6" component="div" sx={{ opacity: 0.9 }}>
                    {title}
                </Typography>
            </Box>
            <Box
                sx={{
                    fontSize: '48px',
                    opacity: 0.8,
                    zIndex: 1,
                }}
            >
                {icon}
            </Box>
        </Paper>
    );
};

const Welcome = () => {
    const translate = useTranslate();
    const [showCreateWizard, setShowCreateWizard] = useState(false);

    const configAxios: AxiosRequestConfig = {
        url: routes.dashboardStatistics(),
        method: 'get',
    };

    const { data: dashboardData, isLoading, error } = useQuery({
        queryKey: ['dashboard/statistics'],
        queryFn: () => fetchWithToken(configAxios),
        refetchInterval: 60000, // Обновляем каждую минуту
        staleTime: 30000, // Данные считаются свежими 30 секунд
    });

    const stats: DashboardStats = dashboardData?.data || { 
        objects_count: 0, 
        devices_count: 0, 
        users_count: 0 
    };

    const handleCreateDashboard = () => {
        setShowCreateWizard(true);
    };

    const handleWizardClose = () => {
        setShowCreateWizard(false);
    };

    const handleWizardSuccess = (dashboardId: string) => {
        setShowCreateWizard(false);
        // Можно добавить переход к созданному дашборду
    };

    return (
        <Box>
            <Card
                sx={{
                    background: theme =>
                        `linear-gradient(45deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.light} 50%, ${theme.palette.primary.dark} 100%)`,
                    color: theme => theme.palette.primary.contrastText,
                    padding: '20px',
                    marginTop: 2,
                    marginBottom: '2em',
                }}
            >
                <Box display="flex">
                    <Box flex="1">
                        <Typography variant="h5" component="h2" gutterBottom>
                            {translate('dashboard.welcome.title', { defaultMessage: 'Dashboard' })}
                        </Typography>
                        <Box maxWidth="40em">
                            <Typography variant="body1" component="p" gutterBottom>
                                {translate('dashboard.welcome.subtitle', { 
                                    defaultMessage: 'Обзор системы в реальном времени' 
                                })}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        display={{ xs: 'none', sm: 'none', md: 'block' }}
                        sx={{
                            background: `url(${publishArticleImage}) top right / cover`,
                            marginLeft: 'auto',
                        }}
                        width="16em"
                        height="9em"
                        overflow="hidden"
                    />
                </Box>
            </Card>

            {/* Статистические карточки */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <Paper sx={{ p: 3, textAlign: 'center', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Paper>
                    ) : (
                        <StatCard
                            title={translate('dashboard.stats.objects', { defaultMessage: 'Объекты' })}
                            value={stats.objects_count}
                            icon={<BusinessIcon />}
                            color="#1976d2"
                        />
                    )}
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <Paper sx={{ p: 3, textAlign: 'center', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Paper>
                    ) : (
                        <StatCard
                            title={translate('dashboard.stats.devices', { defaultMessage: 'Устройства' })}
                            value={stats.devices_count}
                            icon={<DevicesIcon />}
                            color="#388e3c"
                        />
                    )}
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                    {isLoading ? (
                        <Paper sx={{ p: 3, textAlign: 'center', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Paper>
                    ) : (
                        <StatCard
                            title={translate('dashboard.stats.users', { defaultMessage: 'Пользователи' })}
                            value={stats.users_count}
                            icon={<PeopleIcon />}
                            color="#f57c00"
                        />
                    )}
                </Grid>
            </Grid>

            {/* Кнопка создания дашборда */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreateDashboard}
                    size="large"
                >
                    {translate('dashboard.create', { defaultMessage: 'Создать дашборд' })}
                </Button>
            </Box>

            {/* Мастер создания дашборда */}
            <DashboardCreationWizardMUI
                open={showCreateWizard}
                onClose={handleWizardClose}
                onSuccess={handleWizardSuccess}
            />
            {error && (
                <Card sx={{ mt: 2, p: 2, backgroundColor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="body1">
                        {translate('dashboard.error.loading', { 
                            defaultMessage: 'Ошибка загрузки данных дашборда' 
                        })}
                    </Typography>
                </Card>
            )}
        </Box>
    );
};

export default Welcome;
